<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Column from 'primevue/column'
import { ExternalLink, ClipboardPlus } from 'lucide-vue-next'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import DataTable from '@lib/components/shared/DataTable.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import BBadge from '@lib/components/base/BBadge.vue'
import { useFilters, type FilterParser } from '@lib/composables/useFilters'
import { useConfirm } from '@lib/composables/useConfirm'
import { useToast } from '@lib/composables/useToast'
import { useClipboardStore } from '@/stores/clipboard'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useEventOptions } from '@/composables/useLookups'
import { useTicketsetsData, type Ticketset, type TicketsetFilters } from '@/composables/useTicketsetsData'
import TicketsetsFilterBar from '@/components/domain/ticketsets/TicketsetsFilterBar.vue'
import TicketsetEditPanel from '@/components/domain/ticketsets/TicketsetEditPanel.vue'
import TicketsetMobileCard from '@/components/domain/ticketsets/TicketsetMobileCard.vue'
import type { LocationQueryValue } from 'vue-router'

const { t } = useI18n()
const router = useRouter()
const ui = useUiStore()
const auth = useAuthStore()
const { confirm } = useConfirm()
const toast = useToast()
const clipboard = useClipboardStore()

const initialFilters: TicketsetFilters = {
    search: '',
    event: null
}

function asString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string {
    if (Array.isArray(raw)) return raw[0] ?? ''
    return raw ?? ''
}
function asNullableString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
    const v = asString(raw)
    return v === '' ? null : v
}

const parsers: { [K in keyof TicketsetFilters]?: FilterParser<TicketsetFilters[K]> } = {
    event: asNullableString
}

const { filters } = useFilters<TicketsetFilters>(initialFilters, { parsers })

const data = useTicketsetsData()
const eventOptions = useEventOptions()

watch(
    filters,
    v => {
        data.filters.value = { ...v }
    },
    { immediate: true, deep: true }
)

const expandedId = ref<number | null>(null)
// Both desktop (md+) and mobile (< md) branches render in the DOM (only one
// is visible per Tailwind hidden classes). Each branch has its own template
// ref to avoid v-for collisions; activeEditRef picks whichever is currently
// mounted with a panel inside. See EventsView.vue (commit 3a40bae).
type EditPanelHandle = { isDirty: () => boolean }
const editPanelRefDesktop = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
const editPanelRefMobile = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
function unwrap(r: EditPanelHandle | EditPanelHandle[] | null): EditPanelHandle | null {
    if (!r) return null
    if (Array.isArray(r)) return r.find(x => x != null) ?? null
    return r
}
function activeEditRef(): EditPanelHandle | null {
    return unwrap(editPanelRefMobile.value) ?? unwrap(editPanelRefDesktop.value)
}
function safeIsDirty(): boolean {
    try {
        return activeEditRef()?.isDirty() ?? false
    } catch {
        return false
    }
}

const expandedRows = computed<Record<string, boolean>>(() => {
    if (expandedId.value === null) return {}
    return { [String(expandedId.value)]: true }
})

const rows = computed<Ticketset[]>(() => data.filteredTicketsets.value)
const isEmpty = computed(() => !data.loading.value && rows.value.length === 0)

onMounted(async () => {
    ui.setBreadcrumbs([{ label: t('ticketsets.list.title') }])
    await data.refetch()
})

async function toggleExpand(id: number) {
    if (expandedId.value === id) {
        if (safeIsDirty()) {
            const ok = await confirm({
                title: t('ticketsets.confirm.unsavedChanges'),
                severity: 'danger'
            })
            if (!ok) return
        }
        expandedId.value = null
    } else {
        expandedId.value = id
    }
}

function onRowClick(row: Ticketset) {
    toggleExpand(row.id)
}

function clearFilters() {
    filters.value = { ...initialFilters }
}

async function onSaved() {
    expandedId.value = null
    await data.refetch()
}

function onCancel() {
    expandedId.value = null
}

function goToTickets(id: number, ev: Event) {
    ev.stopPropagation()
    router.push({ path: '/tickets', query: { ticketset: String(id) } })
}

function goToTicketsMobile(id: number) {
    router.push({ path: '/tickets', query: { ticketset: String(id) } })
}

function addToClipboard(row: Ticketset, ev?: Event) {
    ev?.stopPropagation()
    clipboard.add({
        kind: 'ticketsets',
        item: {
            id: row.id,
            name: row.name,
            eventName: row.event?.name ?? ''
        }
    })
    toast.success(t('ticketsets.qr.added'))
}

function pct(validated: number, total: number): number {
    if (total === 0) return 0
    return Math.round((validated / total) * 100)
}

watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        data.refetch()
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('ticketsets.list.title')" />

        <TicketsetsFilterBar v-model="filters" :event-options="eventOptions" @clear="clearFilters" />

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="data.loading.value && rows.length === 0" :rows="8" :columns="7" />
            <EmptyState v-else-if="isEmpty" :title="t('ticketsets.list.empty')" />
            <template v-else>
                <!--
                    Both branches render in the DOM (visibility gated by
                    hidden/md:block/md:hidden). Each owns its own
                    TicketsetEditPanel ref to avoid template-ref collisions
                    across two trees.
                -->
                <!-- DESKTOP (md+): table -->
                <div class="hidden md:block">
                    <DataTable
                        :value="rows"
                        row-key="id"
                        :expanded-rows="expandedRows"
                        @row-click="onRowClick"
                    >
                        <Column :header="t('ticketsets.list.cols.name')">
                            <template #body="{ data: row }">
                                <span class="font-medium text-ink">{{ row.name }}</span>
                                <BBadge
                                    v-if="row.bzucoId > 0"
                                    variant="arch"
                                    :label="t('ticketsets.bzucoSynced.badge')"
                                    class="ml-2"
                                />
                            </template>
                        </Column>
                        <Column :header="t('ticketsets.list.cols.event')">
                            <template #body="{ data: row }">
                                <span class="text-sm">{{ row.event?.name ?? '—' }}</span>
                            </template>
                        </Column>
                        <Column :header="t('ticketsets.list.cols.entries')" :style="{ width: '80px' }">
                            <template #body="{ data: row }">
                                <span class="text-xs tabular-nums">{{ row.entries }}</span>
                            </template>
                        </Column>
                        <Column :header="t('ticketsets.list.cols.entryPause')" :style="{ width: '100px' }">
                            <template #body="{ data: row }">
                                <span class="text-xs tabular-nums">{{ row.entryPause }}</span>
                            </template>
                        </Column>
                        <Column :header="t('ticketsets.list.cols.validate')" :style="{ width: '80px' }">
                            <template #body="{ data: row }">
                                <span v-if="row.validate" class="text-teal">✓</span>
                                <span v-else class="text-gray-300">—</span>
                            </template>
                        </Column>
                        <Column :header="t('ticketsets.list.cols.tickets')" :style="{ width: '110px' }">
                            <template #body="{ data: row }">
                                <div class="text-xs tabular-nums">
                                    <div class="font-medium text-ink">
                                        {{ row.validatedCount }} / {{ row.ticketCount }}
                                    </div>
                                    <div class="text-gray-500">{{ pct(row.validatedCount, row.ticketCount) }} %</div>
                                </div>
                            </template>
                        </Column>
                        <Column :style="{ width: '90px' }">
                            <template #body="{ data: row }">
                                <div class="flex items-center gap-1">
                                    <button
                                        type="button"
                                        class="text-gray-500 hover:text-teal transition-colors p-1"
                                        :title="t('ticketsets.qr.addToClipboard')"
                                        @click="(ev: Event) => addToClipboard(row, ev)"
                                    >
                                        <ClipboardPlus class="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        class="text-gray-500 hover:text-teal transition-colors p-1"
                                        :title="t('ticketsets.list.viewTickets')"
                                        @click="(ev: Event) => goToTickets(row.id, ev)"
                                    >
                                        <ExternalLink class="w-4 h-4" />
                                    </button>
                                </div>
                            </template>
                        </Column>
                        <template #expansion="{ data: row }">
                            <ExpandRowPanel
                                :title="t('ticketsets.list.cols.name') + ': ' + row.name"
                                @close="toggleExpand(row.id)"
                            >
                                <TicketsetEditPanel
                                    ref="editPanelRefDesktop"
                                    :ticketset="row"
                                    @saved="onSaved"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </DataTable>
                </div>
                <!-- MOBILE (< md): vertical card list -->
                <ul class="md:hidden divide-y divide-gray-100">
                    <li v-for="row in rows" :key="row.id" class="p-2">
                        <TicketsetMobileCard
                            :ticketset="row"
                            :expanded="expandedId === row.id"
                            @toggle-expand="toggleExpand(row.id)"
                            @view-tickets="goToTicketsMobile"
                            @add-to-clipboard="addToClipboard(row)"
                        />
                        <ExpandRowPanel
                            v-if="expandedId === row.id"
                            :title="t('ticketsets.list.cols.name') + ': ' + row.name"
                            class="mt-2"
                            @close="toggleExpand(row.id)"
                        >
                            <TicketsetEditPanel
                                ref="editPanelRefMobile"
                                :ticketset="row"
                                @saved="onSaved"
                                @cancel="onCancel"
                            />
                        </ExpandRowPanel>
                    </li>
                </ul>
            </template>
        </div>
    </DefaultLayout>
</template>
